import React from "react";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { cn } from "@/utils/utils";

interface LeadGenerationOptionProps {
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  lockReason?: string;
  name: string;
  onSelect: () => void;
  tag?: string;
}

const LeadGenerationOption: React.FC<LeadGenerationOptionProps> = ({
  description,
  icon,
  isSelected,
  lockReason,
  name,
  onSelect,
  tag,
}) => {
  const isLocked = !!lockReason;

  return (
    <button
      aria-disabled={isLocked}
      className={cn(
        "flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors",
        isSelected
          ? "border-indigo-400 bg-indigo-50/60 ring-1 ring-indigo-400 dark:border-indigo-500 dark:bg-indigo-950/30 dark:ring-indigo-500"
          : "border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800",
        isLocked
          ? "cursor-not-allowed opacity-60"
          : !isSelected && "hover:border-gray-300 dark:hover:border-slate-600",
      )}
      onClick={() => {
        if (!isLocked) onSelect();
      }}
      type="button"
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          isSelected
            ? "border-indigo-500 dark:border-indigo-400"
            : "border-gray-300 dark:border-slate-600",
        )}
      >
        {isSelected && (
          <span className="h-2.5 w-2.5 rounded-full bg-indigo-500 dark:bg-indigo-400" />
        )}
      </span>

      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
          isSelected
            ? "border-indigo-200 bg-white text-indigo-500 dark:border-indigo-900 dark:bg-slate-800 dark:text-indigo-400"
            : "border-gray-200 bg-gray-50 text-gray-500 dark:border-slate-700 dark:bg-slate-700/40 dark:text-gray-400",
        )}
      >
        {icon}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-gray-900 dark:text-white">
          {name}
        </span>
        <span className="mt-0.5 block text-sm text-gray-500 dark:text-gray-400">
          {description}
        </span>
      </span>

      {isLocked ? (
        <>
          <span className="sr-only">{lockReason}</span>
          <span
            aria-hidden="true"
            className="hidden shrink-0 items-center gap-1.5 text-xs text-amber-600 sm:inline-flex dark:text-amber-400"
          >
            <LockClosedIcon className="h-3.5 w-3.5" />
            {lockReason}
          </span>
        </>
      ) : (
        tag && (
          <span className="hidden shrink-0 rounded-md border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 sm:inline-block dark:border-green-900 dark:bg-green-950/40 dark:text-green-400">
            {tag}
          </span>
        )
      )}
    </button>
  );
};

export default LeadGenerationOption;
